import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { buildCaregiverMapMarkers } from "../../utils/marketplaceMapPoints";
import {
  CaregiverListingCard,
  formatCaregiverDistance,
  formatCaregiverMoney,
  formatCaregiverRating,
} from "./MarketplaceBrowseUI";
import "./MarketplaceBrowseUI.css";
import "./MarketplaceCaregiverMap.css";

function createCaregiverIcon({ label, selected = false }) {
  return L.divIcon({
    className: "",
    html: `<div class="mp-map-pin${selected ? " is-selected" : ""}" aria-hidden="true"><span>${label}</span></div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20],
  });
}

function createCustomerIcon() {
  return L.divIcon({
    className: "",
    html: '<div class="mp-map-pin mp-map-pin--you" aria-hidden="true"><span></span></div>',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function clampHoverCardPosition(point, wrapSize) {
  const cardW = 480;
  const cardH = 320;
  const pad = 12;
  let left = point.x + 22;
  let top = point.y - Math.round(cardH * 0.35);

  if (left + cardW > wrapSize.width - pad) {
    left = point.x - cardW - 22;
  }
  if (left < pad) left = pad;
  if (top < pad) top = pad;
  if (top + cardH > wrapSize.height - pad) {
    top = Math.max(pad, wrapSize.height - cardH - pad);
  }

  return { left, top };
}

export default function MarketplaceCaregiverMap({
  caregivers = [],
  customerLat,
  customerLng,
  locationLabel = "",
  selectedId = null,
  onSelectCaregiver,
  onViewProfile,
  onHire,
  onToggleFav,
  isFav,
  getInitials,
  formatRating = formatCaregiverRating,
  formatMoney = formatCaregiverMoney,
  planType = "daily",
  height = 520,
}) {
  const containerRef = useRef(null);
  const wrapRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const customerMarkerRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  const [hoverCard, setHoverCard] = useState(null);

  const customerCoords = useMemo(
    () => ({
      latitude: customerLat,
      longitude: customerLng,
    }),
    [customerLat, customerLng]
  );

  const markers = useMemo(
    () => buildCaregiverMapMarkers(caregivers, customerCoords),
    [caregivers, customerCoords]
  );

  const hasCustomer =
    Number.isFinite(Number(customerLat)) && Number.isFinite(Number(customerLng));

  function clearHoverCloseTimer() {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }

  function scheduleHoverClose() {
    clearHoverCloseTimer();
    hoverCloseTimerRef.current = setTimeout(() => {
      setHoverCard(null);
    }, 180);
  }

  function openHoverCard(caregiver, latLng) {
    const map = mapRef.current;
    const wrap = wrapRef.current;
    if (!map || !wrap) return;
    clearHoverCloseTimer();
    const point = map.latLngToContainerPoint(latLng);
    const rect = wrap.getBoundingClientRect();
    const position = clampHoverCardPosition(point, {
      width: rect.width,
      height: rect.height,
    });
    setHoverCard({ caregiver, position });
  }

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const initialCenter = hasCustomer ? [Number(customerLat), Number(customerLng)] : [12.9716, 77.5946];
    const map = L.map(containerRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
    }).setView(initialCenter, hasCustomer ? 13 : 11);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    mapRef.current = map;

    return () => {
      clearHoverCloseTimer();
      map.remove();
      mapRef.current = null;
      markersRef.current = [];
      customerMarkerRef.current = null;
    };
  }, [customerLat, customerLng, hasCustomer]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    if (customerMarkerRef.current) {
      customerMarkerRef.current.remove();
      customerMarkerRef.current = null;
    }

    if (hasCustomer) {
      customerMarkerRef.current = L.marker([Number(customerLat), Number(customerLng)], {
        icon: createCustomerIcon(),
        zIndexOffset: 1000,
      })
        .addTo(map)
        .bindTooltip(locationLabel || "Your location", {
          direction: "top",
          offset: [0, -8],
          opacity: 0.95,
        });
    }

    markers.forEach(({ caregiver, id, lat, lng, approximate }) => {
      const initials = getInitials?.(caregiver.name) || "CG";
      const selected = selectedId != null && String(selectedId) === id;
      const latFixed = Number(lat).toFixed(5);
      const lngFixed = Number(lng).toFixed(5);
      const marker = L.marker([lat, lng], {
        icon: createCaregiverIcon({ label: initials, selected }),
        riseOnHover: true,
      })
        .addTo(map)
        .bindTooltip(
          `<strong>${caregiver.name || "Caregiver"}</strong><br/>Lat ${latFixed}<br/>Lng ${lngFixed}${
            approximate ? "<br/><em>Approximate</em>" : ""
          }`,
          {
            direction: "top",
            offset: [0, -12],
            opacity: 0.95,
            sticky: true,
          }
        );

      marker.on("mouseover", () => {
        openHoverCard(caregiver, marker.getLatLng());
      });
      marker.on("mouseout", () => {
        scheduleHoverClose();
      });
      marker.on("click", () => {
        onSelectCaregiver?.(caregiver);
      });

      markersRef.current.push(marker);
    });

    const hideOnMove = () => setHoverCard(null);
    map.on("movestart", hideOnMove);
    map.on("zoomstart", hideOnMove);

    return () => {
      map.off("movestart", hideOnMove);
      map.off("zoomstart", hideOnMove);
    };
  }, [
    markers,
    hasCustomer,
    customerLat,
    customerLng,
    locationLabel,
    selectedId,
    onSelectCaregiver,
    getInitials,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const boundsPoints = [];
    if (hasCustomer) boundsPoints.push([Number(customerLat), Number(customerLng)]);
    markers.forEach((m) => boundsPoints.push([m.lat, m.lng]));

    if (boundsPoints.length > 1) {
      const bounds = L.latLngBounds(boundsPoints);
      map.fitBounds(bounds.pad(0.16), { maxZoom: 14 });
    } else if (boundsPoints.length === 1) {
      map.setView(boundsPoints[0], 13);
    }
  }, [markers, hasCustomer, customerLat, customerLng]);

  useEffect(() => () => clearHoverCloseTimer(), []);

  const selectedCaregiver = useMemo(() => {
    if (!selectedId) return markers[0]?.caregiver || null;
    return caregivers.find((c) => String(c.id) === String(selectedId)) || markers[0]?.caregiver || null;
  }, [selectedId, caregivers, markers]);

  if (!hasCustomer) {
    return <p className="mp-map-empty">Set your location to view caregivers on the map.</p>;
  }

  if (!markers.length) {
    return <p className="mp-map-empty">No caregivers to show on the map for this search.</p>;
  }

  const hovered = hoverCard?.caregiver || null;
  const favForHovered = hovered
    ? typeof isFav === "function"
      ? isFav(hovered)
      : Boolean(isFav)
    : false;

  return (
    <div className="mp-map-layout">
      <div className="mp-map-wrap" ref={wrapRef}>
        <div ref={containerRef} className="mp-map-canvas" style={{ height }} />
        <div className="mp-map-legend" aria-hidden="true">
          <span className="mp-map-legend__dot mp-map-legend__dot--you" />
          You
          <span className="mp-map-legend__dot mp-map-legend__dot--cg" />
          Caregivers
        </div>

        {hovered && hoverCard?.position ? (
          <div
            className="mp-map-hover-card"
            style={{
              left: hoverCard.position.left,
              top: hoverCard.position.top,
            }}
            onMouseEnter={clearHoverCloseTimer}
            onMouseLeave={scheduleHoverClose}
          >
            <CaregiverListingCard
              caregiver={hovered}
              getInitials={getInitials}
              formatRating={formatRating}
              formatMoney={formatMoney}
              planType={planType}
              onViewProfile={onViewProfile}
              onHire={onHire}
              isFav={favForHovered}
              onToggleFav={onToggleFav}
            />
          </div>
        ) : null}
      </div>

      {selectedCaregiver ? (
        <article className="mp-map-sheet">
          <div className="mp-map-sheet__main">
            <strong>{selectedCaregiver.name}</strong>
            <p>{selectedCaregiver.designation || "Care professional"}</p>
            <p className="mp-map-sheet__meta">
              ★ {formatRating(selectedCaregiver.rating, selectedCaregiver.reviewCount)}
              {formatCaregiverDistance(selectedCaregiver.distanceKm)
                ? ` · ${formatCaregiverDistance(selectedCaregiver.distanceKm)}`
                : ""}
            </p>
            {(() => {
              const pin = markers.find((m) => String(m.id) === String(selectedCaregiver.id));
              if (!pin) return null;
              const latFixed = Number(pin.lat).toFixed(5);
              const lngFixed = Number(pin.lng).toFixed(5);
              return (
                <p className="mp-map-sheet__coords" title={pin.approximate ? "Approximate position" : "Exact location"}>
                  {pin.approximate ? "Approx. " : ""}
                  {latFixed}, {lngFixed}
                </p>
              );
            })()}
          </div>
          <div className="mp-map-sheet__actions">
            <button type="button" className="mp-map-sheet__btn" onClick={() => onViewProfile?.(selectedCaregiver)}>
              View profile
            </button>
            <button
              type="button"
              className="mp-map-sheet__btn mp-map-sheet__btn--primary"
              onClick={() => onHire?.(selectedCaregiver)}
            >
              Hire
            </button>
          </div>
        </article>
      ) : null}
    </div>
  );
}
